import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { parseApiError } from '@saas-kit/lib';
import { toast } from '@saas-kit/ui';

import { printToken } from './apiPrint';
import { PrintError, isWebSerialSupported, printEscPos } from './escposPrinter';
import { printReceipt } from './printReceipt';

interface PrintOptions {
  /** True for a button press (may raise the printer picker); false for the
   *  automatic print fired on mount, which has no user gesture. */
  interactive?: boolean;
}

/**
 * Print a queue token receipt.
 *
 * Asks the backend to render the token to ESC/POS; if the server delivered it
 * to a network printer the job is done, otherwise the returned payload is
 * streamed to the workstation's serial printer. Where Web Serial is
 * unavailable it degrades to the legacy OS print dialog so printing never
 * hard-fails.
 */
export function usePrintToken(): {
  print: (tokenId: string, options?: PrintOptions) => Promise<void>;
  isPrinting: boolean;
} {
  const { t } = useTranslation('qms');
  const [isPrinting, setIsPrinting] = useState(false);

  const print = useCallback(
    async (tokenId: string, options?: PrintOptions): Promise<void> => {
      const interactive = options?.interactive ?? true;
      setIsPrinting(true);
      try {
        const result = await printToken(tokenId);
        if (result.sent) {
          toast.success(t('print.toasts.sent'));
          return;
        }
        if (result.transport === 'agent') {
          // The backend owns delivery with the `agent` transport — it just
          // couldn't reach the store's print agent. Say so rather than
          // popping the browser dialog the agent was built to replace.
          toast.error(t('print.toasts.agent_offline'));
          return;
        }
        if (!isWebSerialSupported()) {
          // `noop` transport, no raw-printer path here — fall back to the dialog.
          printReceipt();
          return;
        }
        await printEscPos(result.payload_base64, { allowPrompt: interactive });
        toast.success(t('print.toasts.sent'));
      } catch (error) {
        if (error instanceof PrintError) {
          if (error.code === 'cancelled') return; // operator dismissed picker
          if (error.code === 'unsupported') {
            printReceipt();
            return;
          }
          if (error.code === 'no-printer') {
            // Auto-print before any printer was authorised this session.
            toast.info(t('print.toasts.no_printer'));
            return;
          }
          toast.error(t('print.toasts.failed'));
          return;
        }
        toast.error(parseApiError(error).message);
      } finally {
        setIsPrinting(false);
      }
    },
    [t],
  );

  return { print, isPrinting };
}
